import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useIsMobile } from "@/hooks/use-mobile";
import { buyerListService } from "@/services/buyerListService";
import { buyerExportService } from "@/services/buyerExportService";
import { BuyerList, BuyerListItem } from "@/types/buyerSearch";
import { ArrowLeft, Users, Calendar, Mail, Phone, Building2, Trash2, Download, Pencil, MapPin } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import SellerRestrictedPage from "@/components/seller/SellerRestrictedPage";
import { EditManualBuyerDialog } from "@/components/seller/dialogs/EditManualBuyerDialog";
import { BuyerViewToggle } from "@/components/seller/BuyerViewToggle";
import { BuyerListViewRow } from "@/components/seller/search/BuyerListViewRow";
import { BuyerDetailModal } from "@/components/seller/dialogs/BuyerDetailModal";
import { supabase } from "@/integrations/supabase/client";
import { safeStorage } from "@/utils/safariStorage";

const BuyerListDetailPage = () => {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [list, setList] = useState<BuyerList | null>(null);
  const [buyers, setBuyers] = useState<BuyerListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingBuyer, setEditingBuyer] = useState<any | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'tile'>('tile');
  const [selectedBuyer, setSelectedBuyer] = useState<BuyerListItem | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  usePageTitle(list?.list_name || "Buyer List Details");

  const fetchListDetails = async () => {
    if (!listId) return;
    
    setIsLoading(true);
    try {
      const [listResult, buyersResult] = await Promise.all([
        buyerListService.getListDetails(listId),
        buyerListService.getBuyersInList(listId)
      ]);

      if (listResult.error) {
        toast({
          title: "Error",
          description: listResult.error,
          variant: "destructive"
        });
        navigate("/seller/buyer-lists");
        return;
      }

      if (buyersResult.error) {
        toast({
          title: "Error",
          description: buyersResult.error,
          variant: "destructive"
        });
      }

      setList(listResult.data);
      setBuyers(buyersResult.data || []);
    } catch (error) {
      console.error('Error fetching list details:', error);
      toast({
        title: "Error",
        description: "Failed to load list details",
        variant: "destructive"
      });
      navigate("/seller/buyer-lists");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchListDetails();
  }, [listId]);

  useEffect(() => {
    const savedView = safeStorage.getItem('buyerListViewMode');
    if (savedView === 'list' || savedView === 'tile') {
      setViewMode(savedView);
    }
  }, []);

  const handleViewModeChange = (mode: 'list' | 'tile') => {
    setViewMode(mode);
    safeStorage.setItem('buyerListViewMode', mode);
  };

  const handleRemoveBuyer = async (buyerId: string) => {
    if (!listId) return;
    
    if (window.confirm("Are you sure you want to remove this buyer from the list?")) {
      try {
        const { success, error } = await buyerListService.removeBuyerFromList(listId, buyerId);
        
        if (success) {
          setBuyers(prev => prev.filter(buyer => buyer.buyer_id !== buyerId));
          toast({
            title: "Success",
            description: "Buyer removed from list"
          });
        } else {
          toast({
            title: "Error",
            description: error || "Failed to remove buyer",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Error removing buyer:', error);
        toast({
          title: "Error",
          description: "Failed to remove buyer from list",
          variant: "destructive"
        });
      }
    }
  };

  const handleDownloadList = () => {
    if (!list || buyers.length === 0) {
      toast({
        title: "No Data",
        description: "No buyers in this list to export",
        variant: "destructive",
      });
      return;
    }

    try {
      buyerExportService.exportBuyerListToCSV(buyers, list.list_name);
      toast({
        title: "Success",
        description: "Buyer list exported successfully",
      });
    } catch (error) {
      console.error('Error exporting list:', error);
      toast({
        title: "Error",
        description: "Failed to export buyer list",
        variant: "destructive",
      });
    }
  };

  const fetchSellerCreatedBuyerDetails = async (sellerCreatedBuyerId: string) => {
    try {
      const { data, error } = await (supabase as any)
        .from('seller_created_buyers')
        .select('*')
        .eq('id', sellerCreatedBuyerId)
        .single();
      
      if (error) {
        toast({ 
          title: "Error", 
          description: "Failed to load buyer details", 
          variant: "destructive" 
        });
        return null;
      }
      return data;
    } catch (error) {
      console.error('Error fetching seller-created buyer:', error);
      return null;
    }
  };

  const handleEditBuyer = async (item: BuyerListItem) => {
    if (item.buyer_type === 'private' && item.seller_created_buyer_id) {
      const buyerDetails = await fetchSellerCreatedBuyerDetails(item.seller_created_buyer_id);
      if (buyerDetails) {
        setEditingBuyer(buyerDetails);
        setShowEditDialog(true);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="py-6">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading list details...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="py-6">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-muted-foreground">List not found</p>
              <Button 
                onClick={() => navigate("/seller/buyer-lists")} 
                className="mt-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Lists
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <SellerRestrictedPage featureName="buyer list details">
      <div className="py-6 space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/seller/buyer-lists")}
                className="w-full sm:w-auto"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {isMobile ? "Back" : "Back to Lists"}
              </Button>
              <div>
                <CardTitle className="text-xl sm:text-2xl">{list.list_name}</CardTitle>
                {list.description && (
                  <p className="text-muted-foreground mt-1">{list.description}</p>
                )}
              </div>
            </div>
            <Button
              onClick={() => navigate(`/seller/buyer-lists/${listId}/edit`)}
              className="w-full sm:w-auto"
            >
              {isMobile ? "Edit" : "Edit List"}
            </Button>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 text-sm text-muted-foreground pt-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>{buyers.length} buyers</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Created {format(new Date(list.created_at), 'MMM d, yyyy')}</span>
            </div>
          </div>
          
          <div className="pt-2 flex gap-2">
            <BuyerViewToggle value={viewMode} onChange={handleViewModeChange} />
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadList}
              disabled={buyers.length === 0}
              className="w-full sm:w-auto"
            >
              <Download className="h-4 w-4 mr-2" />
              {isMobile ? "Download" : "Download List"}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Buyers List */}
      {buyers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No buyers in this list</h3>
            <p className="text-muted-foreground text-center mb-4">
              Add platform buyers from the search page or create private buyer contacts.
            </p>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/seller/search-buyers')}>
                Search Platform Buyers
              </Button>
              <Button onClick={() => navigate('/seller/buyer-lists')} variant="outline">
                Add Private Buyer
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === 'tile' ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {buyers.map((item) => {
                  const buyer = item.buyer_profile;
                  const isPrivate = item.buyer_type === 'private';

                  const getInitials = (name: string) => {
                    return name
                      .split(' ')
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);
                  };

                  return (
                    <Card key={item.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                      <CardHeader className="pb-4">
                        <div className="flex items-start gap-4">
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={buyer?.profile_image_url} alt={buyer?.full_name} />
                            <AvatarFallback>
                              {getInitials(buyer?.full_name || 'N/A')}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <CardTitle className="text-lg">{buyer?.full_name || 'N/A'}</CardTitle>
                            {buyer?.company_name && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                <Building2 className="h-4 w-4" />
                                <span>{buyer.company_name}</span>
                              </div>
                            )}
                          </div>
                          <Badge variant={isPrivate ? "secondary" : "default"}>
                            {isPrivate ? "Private" : "Platform"}
                          </Badge>
                        </div>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          {buyer?.email && (
                            <div className="flex items-center gap-2 text-sm">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              <a
                                href={`mailto:${buyer.email}`}
                                className="text-primary hover:underline"
                              >
                                {buyer.email}
                              </a>
                            </div>
                          )}
                          {buyer?.phone_number && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <a
                                href={`tel:${buyer.phone_number}`}
                                className="text-primary hover:underline"
                              >
                                {buyer.phone_number}
                              </a>
                            </div>
                          )}
                        </div>

                        <Separator />

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground">Deals Annually</p>
                            <p className="font-semibold">{buyer?.deals_annually || 'N/A'}</p>
                          </div>
                          {buyer?.average_close_time && (
                            <div>
                              <p className="text-xs text-muted-foreground">Avg. Close Time</p>
                              <p className="font-semibold">{buyer.average_close_time}</p>
                            </div>
                          )}
                        </div>

                        {buyer?.purchasing_type && Array.isArray(buyer.purchasing_type) && buyer.purchasing_type.length > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-2">Purchasing Types</p>
                            <div className="flex flex-wrap gap-2">
                              {buyer.purchasing_type.map((type: string) => (
                                <Badge key={type} variant="outline" className="text-xs">
                                  {type}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {buyer?.preferred_markets && Array.isArray(buyer.preferred_markets) && buyer.preferred_markets.length > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-2">Preferred Markets</p>
                            <div className="flex flex-wrap gap-2">
                              {buyer.preferred_markets.map((market: string) => (
                                <Badge key={market} variant="secondary" className="text-xs">
                                  <MapPin className="h-3 w-3 mr-1" />
                                  {market}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {item.notes && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Notes</p>
                            <p className="text-sm text-muted-foreground">{item.notes}</p>
                          </div>
                        )}

                        <div className="text-xs text-muted-foreground">
                          Added {item.added_at ? format(new Date(item.added_at), 'MMM d, yyyy') : 'N/A'}
                        </div>
                      </CardContent>

                      <CardFooter className="flex gap-2 pt-4 border-t">
                        {isPrivate && (
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => handleEditBuyer(item)}
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </Button>
                        )}
                        <Button
                          variant="destructive"
                          className="flex-1"
                          onClick={() => handleRemoveBuyer(item.buyer_id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove
                        </Button>
                      </CardFooter>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {buyers.map((item) => (
                      <BuyerListViewRow
                        key={item.id}
                        item={item}
                        onViewDetails={() => {
                          setSelectedBuyer(item);
                          setShowDetailModal(true);
                        }}
                        onEdit={handleEditBuyer}
                        onRemove={handleRemoveBuyer}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

      {/* Edit Manual Buyer Dialog */}
      <EditManualBuyerDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        buyer={editingBuyer}
        onBuyerUpdated={fetchListDetails}
      />

      {/* Buyer Detail Modal */}
      <BuyerDetailModal
        open={showDetailModal}
        onOpenChange={setShowDetailModal}
        buyerItem={selectedBuyer}
        onEdit={handleEditBuyer}
        onRemove={handleRemoveBuyer}
      />
      </div>
    </SellerRestrictedPage>
  );
};

export default BuyerListDetailPage;